import type { ICompasAIChatAction, ICompassMessage } from "@/types/compass.types";

export const COMPASS_STREAM_TIMEOUT_MS = 90_000;

export interface CompassStreamResult {
  actions: ICompasAIChatAction[];
  text: string;
  error: string | null;
}

interface StreamPayload {
  type?: string;
  response?: unknown;
  suggested_next_steps?: ICompasAIChatAction[];
}

function asActionList(value: unknown): ICompasAIChatAction[] {
  return Array.isArray(value) ? (value as ICompasAIChatAction[]) : [];
}

function parseStreamLine(line: string): StreamPayload | null {
  const trimmed = line.trim();
  if (trimmed === "") return null;
  const payload = trimmed.startsWith("data:") ? trimmed.slice(5).trim() : trimmed;
  if (payload === "" || payload === "[DONE]" || payload.startsWith(":")) return null;
  try {
    const data: unknown = JSON.parse(payload);
    if (typeof data === "object" && data != null) return data as StreamPayload;
  } catch {
    return null;
  }
  return null;
}

export function consumeCompassText(
  raw: string,
  onText?: (text: string) => void
): CompassStreamResult {
  const actions: ICompasAIChatAction[] = [];
  let text = "";
  let error: string | null = null;
  for (const line of raw.split("\n")) {
    const parsed = parseStreamLine(line);
    if (parsed == null) continue;
    const next = applyPayload(parsed, actions, onText);
    if (next.text != null) text = next.text;
    if (next.error != null) error = next.error;
  }
  return { actions, text, error };
}

function applyPayload(
  parsed: StreamPayload,
  actions: ICompasAIChatAction[],
  onText: ((text: string) => void) | undefined
): { text?: string; error?: string } {
  if (parsed.type === "action") {
    const next = [...asActionList(parsed.response), ...asActionList(parsed.suggested_next_steps)];
    actions.push(...next);
    return {};
  }
  if (typeof parsed.response === "string" && parsed.response.trim() !== "") {
    onText?.(parsed.response);
    if (parsed.type === "error") return { text: parsed.response, error: parsed.response };
    return { text: parsed.response };
  }
  return {};
}

export function createCompassDeadline(timeoutMs = COMPASS_STREAM_TIMEOUT_MS): {
  signal: AbortSignal;
  cancel: () => void;
} {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  return {
    signal: controller.signal,
    cancel: () => clearTimeout(timeoutId),
  };
}

export function upsertOptimisticAssistant(
  prev: ICompassMessage[],
  text: string
): ICompassMessage[] {
  const last = prev[prev.length - 1];
  if (last?.isSender === false) {
    return [...prev.slice(0, -1), { ...last, text }];
  }
  return [
    ...prev,
    { isSender: false, canCopy: true, text, creation_date: new Date().toISOString() },
  ];
}

const COMPASS_TIMEOUT_MESSAGE = "Compass took too long to reply. Please try again.";

function rejectOnStreamDeadline(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  timeoutMs: number,
  signal: AbortSignal | undefined
): { promise: Promise<never>; cancel: () => void } {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const abortRead = (reject: (error: Error) => void) => {
    void reader.cancel().catch(() => undefined);
    reject(new Error(COMPASS_TIMEOUT_MESSAGE));
  };
  let onAbort: (() => void) | undefined;
  const promise = new Promise<never>((_, reject) => {
    if (signal?.aborted === true) {
      abortRead(reject);
      return;
    }
    if (timeoutMs > 0) timeoutId = setTimeout(() => abortRead(reject), timeoutMs);
    onAbort = () => abortRead(reject);
    signal?.addEventListener("abort", onAbort, { once: true });
  });
  return {
    promise,
    cancel: () => {
      if (timeoutId != null) clearTimeout(timeoutId);
      if (onAbort != null) signal?.removeEventListener("abort", onAbort);
    },
  };
}

/**
 * Compass streams newline-delimited JSON (and sometimes `data:` SSE).
 * Question/plan tokens are not `type: "action"` — treating only actions as
 * success left the pane on "AI is thinking" until the socket closed, which
 * on Word WebView may never happen.
 */
export async function processCompassStream(
  stream: ReadableStream<Uint8Array>,
  options?: { timeoutMs?: number; onText?: (text: string) => void; signal?: AbortSignal }
): Promise<CompassStreamResult> {
  const reader = stream.getReader();
  const decoder = new TextDecoder();
  const actions: ICompasAIChatAction[] = [];
  let text = "";
  let error: string | null = null;
  let buffer = "";
  const timeoutMs = options?.timeoutMs ?? COMPASS_STREAM_TIMEOUT_MS;
  const watchdog =
    timeoutMs > 0 || options?.signal != null
      ? rejectOnStreamDeadline(reader, timeoutMs, options?.signal)
      : null;

  const readAll = async () => {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() ?? "";
      for (const line of lines) {
        const parsed = parseStreamLine(line);
        if (parsed == null) continue;
        const next = applyPayload(parsed, actions, options?.onText);
        if (next.text != null) text = next.text;
        if (next.error != null) error = next.error;
      }
    }
    if (buffer.trim() !== "") {
      const parsed = parseStreamLine(buffer);
      if (parsed != null) {
        const next = applyPayload(parsed, actions, options?.onText);
        if (next.text != null) text = next.text;
        if (next.error != null) error = next.error;
      }
    }
  };

  try {
    if (watchdog == null) await readAll();
    else await Promise.race([readAll(), watchdog.promise]);
  } finally {
    watchdog?.cancel();
    try {
      reader.releaseLock();
    } catch {
      /* already released after cancel */
    }
  }
  return { actions, text, error };
}

export function shouldShowCompassThinking(
  isAssistantTyping: boolean,
  messages: ICompassMessage[]
): boolean {
  if (!isAssistantTyping) return false;
  return messages[messages.length - 1]?.isSender !== false;
}

export function compassStreamUserError(result: CompassStreamResult): string | null {
  if (result.error != null && result.error.trim() !== "") return result.error;
  if (result.text.trim() === "" && result.actions.length === 0) {
    return "Compass didn't return a reply. Please try again in a moment.";
  }
  return null;
}
